import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/**
 * Project asset storage — mirrors stacks-agents `src/app/api/storage/`.
 *
 * Storage was the last thing the builder talked to Supabase about directly.
 * The backend holds the only credentials and rebuilds every storage path from
 * the project it has verified the caller owns, so the paths passed from here
 * are relative to the project folder.
 */
@Injectable({ providedIn: 'root' })
export class StorageApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  /**
   * Uploads a file into the storage folder of the project.
   *
   * Note there is no catchError here: uploads are user-initiated and callers
   * surface the failure, so this one rejects rather than resolving to a fallback.
   * Content-Type is deliberately left unset so the browser writes the multipart
   * boundary itself.
   *
   * @param file - The file to upload
   * @param projectId - Owning project (or "admin" for shared template assets)
   * @param path - Path within the project folder, e.g. "images/products/a.png"
   * @returns The public URL of the uploaded file
   */
  async upload(file: File, projectId: string | number, path: string): Promise<string> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('projectId', String(projectId))
    formData.append('path', path)

    const body = await this.http
      .post<{ data: { path: string; url: string } }>(`${environment.agentsUrl}v4/creation/storage/upload`, formData, {
        headers: this.agentsAuthHeaders(),
      })
      .toPromise()
    return body?.data?.url as string
  }

  /**
   * Deletes one of the files of the project. Never throws — matches the previous
   * behaviour, where a failed delete only warned so callers could carry on.
   *
   * @param projectId - Owning project
   * @param urlOrPath - A public URL previously returned by upload(), or a path
   */
  async deleteFile(projectId: string | number, urlOrPath: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/storage/file`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId: String(projectId), urlOrPath },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /**
   * Photos a customer attached to one product of one order.
   * Returns [] on failure, as the direct Supabase listing did.
   */
  getOrderPhotos(projectId: string | number, userId: string, productId: string): Promise<{ path: string; url: string }[]> {
    return this.http
      .get<{ data?: { path: string; url: string }[] }>(`${environment.agentsUrl}v4/retrieval/storage/order-photos`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId: String(projectId), userId, productId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<{ path: string; url: string }[]>
  }
}
